import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SpeedIcon from '@mui/icons-material/Speed';
import React, { useMemo, useState } from 'react';
import { Chain, ChainPicker, loadChain, saveChain } from '../../components/common/ChainPicker';
import { StatCard } from '../../components/common/StatCard';
import { useStaggeredEntrance } from '../../hooks/useStaggeredEntrance';
import { ForgeStatCard } from '../../types';

// ===== Tabs =====
type TabKey = 'overview' | 'labelers' | 'queues';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'labelers', label: 'Labeler Performance' },
  { key: 'queues', label: 'Queue Operations' },
];

// ===== Overview stat cards =====
const OVERVIEW_STATS: ForgeStatCard[] = [
  { label: 'Tasks Today', value: 487, format: 'number', diff: 63, diffLabel: 'vs yesterday' },
  { label: 'Avg Throughput', value: 24.3, format: 'decimal', diffLabel: 'tasks/hr' },
  { label: 'Active Labelers', value: 7, format: 'number', accent: true },
  {
    label: 'Avg QA Accuracy',
    value: 93.2,
    format: 'percent',
    diff: 1.4,
    diffLabel: 'vs last week',
  },
];

// ===== Queue health heatmap data =====
const QUEUE_NAMES = [
  'design_review',
  'design_curator',
  'uncertain_classification',
  'fd_classification',
] as const;
const CHAIN_COLUMNS = ['Lidl GB', 'Lidl DE', 'Morrisons', 'Asda'] as const;

type HealthStatus = 'healthy' | 'attention' | 'critical' | 'disabled';

const HEATMAP_DATA: Record<string, Record<string, HealthStatus>> = {
  design_review: {
    'Lidl GB': 'healthy',
    'Lidl DE': 'healthy',
    Morrisons: 'healthy',
    Asda: 'attention',
  },
  design_curator: {
    'Lidl GB': 'healthy',
    'Lidl DE': 'attention',
    Morrisons: 'healthy',
    Asda: 'healthy',
  },
  uncertain_classification: {
    'Lidl GB': 'attention',
    'Lidl DE': 'healthy',
    Morrisons: 'healthy',
    Asda: 'critical',
  },
  fd_classification: {
    'Lidl GB': 'healthy',
    'Lidl DE': 'attention',
    Morrisons: 'healthy',
    Asda: 'healthy',
  },
};

function healthColor(status: HealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'var(--positive)';
    case 'attention':
      return 'var(--warning)';
    case 'critical':
      return 'var(--danger)';
    case 'disabled':
      return 'var(--text-tertiary)';
  }
}

function healthLabel(status: HealthStatus): string {
  switch (status) {
    case 'healthy':
      return 'Healthy';
    case 'attention':
      return 'Attention';
    case 'critical':
      return 'Critical';
    case 'disabled':
      return 'Disabled';
  }
}

// ===== Labeler Performance data =====
interface LabelerRow {
  name: string;
  tasksToday: number;
  tasksWeek: number;
  avgTime: number;
  qaAccuracy: number;
  skipRate: number;
  primaryQueue: string;
}

const LABELERS: LabelerRow[] = [
  {
    name: 'Alice Chen',
    tasksToday: 72,
    tasksWeek: 312,
    avgTime: 11,
    qaAccuracy: 97.2,
    skipRate: 2,
    primaryQueue: 'design_review',
  },
  {
    name: 'Bob Martinez',
    tasksToday: 58,
    tasksWeek: 267,
    avgTime: 14,
    qaAccuracy: 94.1,
    skipRate: 4,
    primaryQueue: 'design_curator',
  },
  {
    name: 'Carol Singh',
    tasksToday: 45,
    tasksWeek: 198,
    avgTime: 18,
    qaAccuracy: 91.5,
    skipRate: 6,
    primaryQueue: 'fd_classification',
  },
  {
    name: 'David Kim',
    tasksToday: 63,
    tasksWeek: 289,
    avgTime: 9,
    qaAccuracy: 98.7,
    skipRate: 1,
    primaryQueue: 'uncertain_classification',
  },
  {
    name: 'Eva Novak',
    tasksToday: 34,
    tasksWeek: 156,
    avgTime: 25,
    qaAccuracy: 88.3,
    skipRate: 8,
    primaryQueue: 'design_review',
  },
  {
    name: 'Frank Liu',
    tasksToday: 51,
    tasksWeek: 234,
    avgTime: 12,
    qaAccuracy: 95.8,
    skipRate: 3,
    primaryQueue: 'design_review',
  },
  {
    name: 'Grace Okafor',
    tasksToday: 41,
    tasksWeek: 178,
    avgTime: 22,
    qaAccuracy: 90.2,
    skipRate: 7,
    primaryQueue: 'design_curator',
  },
  {
    name: 'Hasan Yilmaz',
    tasksToday: 18,
    tasksWeek: 82,
    avgTime: 38,
    qaAccuracy: 83.6,
    skipRate: 14,
    primaryQueue: 'design_review',
  },
  {
    name: 'Isabella Rossi',
    tasksToday: 29,
    tasksWeek: 131,
    avgTime: 31,
    qaAccuracy: 86.9,
    skipRate: 11,
    primaryQueue: 'fd_classification',
  },
  {
    name: 'Jake Thompson',
    tasksToday: 12,
    tasksWeek: 45,
    avgTime: 45,
    qaAccuracy: 82.1,
    skipRate: 15,
    primaryQueue: 'uncertain_classification',
  },
];

type SortColumn = keyof LabelerRow;
type SortDir = 'asc' | 'desc';

function comparator(a: LabelerRow, b: LabelerRow, col: SortColumn, dir: SortDir): number {
  const av = a[col];
  const bv = b[col];
  let cmp: number;
  if (typeof av === 'string' && typeof bv === 'string') {
    cmp = av.localeCompare(bv);
  } else {
    cmp = (av as number) - (bv as number);
  }
  return dir === 'asc' ? cmp : -cmp;
}

// ===== Queue Operations data =====
interface QueueOpsData {
  name: string;
  displayName: string;
  pending: number;
  drainRate: number;
  skipRate: number;
  avgTime: number;
  lastRegen: string;
}

const QUEUE_OPS: QueueOpsData[] = [
  {
    name: 'design_review',
    displayName: 'Design Review',
    pending: 201,
    drainRate: 11,
    skipRate: 12,
    avgTime: 42,
    lastRegen: '2026-03-11 22:00',
  },
  {
    name: 'design_curator',
    displayName: 'Design Curator',
    pending: 389,
    drainRate: 14,
    skipRate: 8,
    avgTime: 34,
    lastRegen: '2026-03-12 06:30',
  },
  {
    name: 'uncertain_classification',
    displayName: 'Uncertain Classification',
    pending: 445,
    drainRate: 28,
    skipRate: 4,
    avgTime: 6,
    lastRegen: '2026-03-12 09:02',
  },
  {
    name: 'fd_classification',
    displayName: 'FD Classification',
    pending: 134,
    drainRate: 8,
    skipRate: 2,
    avgTime: 22,
    lastRegen: 'event-driven',
  },
];

// ===== Shared styles =====
const monoStyle: React.CSSProperties = { fontFamily: 'var(--font-mono)' };
const fadeTransition = (visible: boolean): React.CSSProperties => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(8px)',
  transition: 'opacity 350ms ease, transform 350ms ease',
});

// ===== Sub-components =====

const HealthBadge: React.FC<{ status: HealthStatus }> = ({ status }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 9999,
      fontSize: 11,
      fontWeight: 600,
      color: healthColor(status),
      background: `color-mix(in srgb, ${healthColor(status)} 14%, transparent)`,
      textTransform: 'capitalize',
    }}
  >
    {healthLabel(status)}
  </span>
);

const SortIcon: React.FC<{ active: boolean; dir: SortDir }> = ({ active, dir }) => {
  if (!active)
    return (
      <FilterListIcon
        style={{
          fontSize: 14,
          color: 'var(--text-tertiary)',
          marginLeft: 4,
          verticalAlign: 'middle',
        }}
      />
    );
  const Icon = dir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Icon
      style={{ fontSize: 14, color: 'var(--accent)', marginLeft: 4, verticalAlign: 'middle' }}
    />
  );
};

const QueueMiniBar: React.FC<{ pending: number; maxPending: number }> = ({
  pending,
  maxPending,
}) => {
  const pct = Math.min((pending / maxPending) * 100, 100);
  const barColor = pct > 80 ? 'var(--danger)' : pct > 50 ? 'var(--warning)' : 'var(--accent)';
  return (
    <div
      style={{
        height: 6,
        borderRadius: 3,
        background: 'var(--border)',
        width: '100%',
        marginTop: 8,
      }}
    >
      <div
        style={{
          height: '100%',
          borderRadius: 3,
          background: barColor,
          width: `${pct}%`,
          transition: 'width 600ms ease',
        }}
      />
    </div>
  );
};

// ===== Main component =====

export const LabelingAdminDashboard: React.FC = () => {
  const [chain, setChain] = useState<Chain>(loadChain);
  const [tab, setTab] = useState<TabKey>('overview');
  const [sortCol, setSortCol] = useState<SortColumn>('tasksToday');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const visibility = useStaggeredEntrance(8, 50);

  const handleChainChange = (c: Chain) => {
    setChain(c);
    saveChain(c);
  };

  const handleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir(col === 'name' ? 'asc' : 'desc');
    }
  };

  const sortedLabelers = useMemo(
    () => [...LABELERS].sort((a, b) => comparator(a, b, sortCol, sortDir)),
    [sortCol, sortDir]
  );

  const maxPending = Math.max(...QUEUE_OPS.map((q) => q.pending));

  const thStyle = (col: SortColumn): React.CSSProperties => ({
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  });

  return (
    <div className="page-content">
      {/* ===== Top bar: chain selector ===== */}
      <div style={{ marginBottom: 20 }}>
        <ChainPicker value={chain} onChange={handleChainChange} />
      </div>

      {/* ===== Tab switcher ===== */}
      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 24,
          borderBottom: '1px solid var(--border)',
        }}
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              color: tab === t.key ? 'var(--accent)' : 'var(--text-secondary)',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--accent)' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all 150ms ease',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ===== Tab: Overview ===== */}
      {tab === 'overview' && (
        <>
          <div className="stats-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {OVERVIEW_STATS.map((stat, i) => (
              <StatCard key={stat.label} stat={stat} isVisible={visibility[i]} />
            ))}
          </div>

          <div style={fadeTransition(visibility[4])}>
            <div className="forge-card" style={{ marginTop: 4 }}>
              <div className="forge-card-title">Queue Health Heatmap</div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Queue</th>
                    {CHAIN_COLUMNS.map((c) => (
                      <th key={c} style={{ textAlign: 'center' }}>
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {QUEUE_NAMES.map((q) => (
                    <tr key={q}>
                      <td style={{ fontWeight: 600 }}>{q.replace(/_/g, ' ')}</td>
                      {CHAIN_COLUMNS.map((c) => (
                        <td key={c} style={{ textAlign: 'center' }}>
                          <HealthBadge status={HEATMAP_DATA[q][c]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===== Tab: Labeler Performance ===== */}
      {tab === 'labelers' && (
        <div style={fadeTransition(visibility[1])}>
          <div className="forge-card">
            <div className="forge-card-title">Labeler Performance</div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={thStyle('name')} onClick={() => handleSort('name')}>
                    Labeler <SortIcon active={sortCol === 'name'} dir={sortDir} />
                  </th>
                  <th style={thStyle('tasksToday')} onClick={() => handleSort('tasksToday')}>
                    Tasks Today <SortIcon active={sortCol === 'tasksToday'} dir={sortDir} />
                  </th>
                  <th style={thStyle('tasksWeek')} onClick={() => handleSort('tasksWeek')}>
                    Tasks This Week <SortIcon active={sortCol === 'tasksWeek'} dir={sortDir} />
                  </th>
                  <th style={thStyle('avgTime')} onClick={() => handleSort('avgTime')}>
                    Avg Time (s) <SortIcon active={sortCol === 'avgTime'} dir={sortDir} />
                  </th>
                  <th style={thStyle('qaAccuracy')} onClick={() => handleSort('qaAccuracy')}>
                    QA Accuracy <SortIcon active={sortCol === 'qaAccuracy'} dir={sortDir} />
                  </th>
                  <th style={thStyle('skipRate')} onClick={() => handleSort('skipRate')}>
                    Skip Rate <SortIcon active={sortCol === 'skipRate'} dir={sortDir} />
                  </th>
                  <th style={thStyle('primaryQueue')} onClick={() => handleSort('primaryQueue')}>
                    Primary Queue <SortIcon active={sortCol === 'primaryQueue'} dir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedLabelers.map((row) => {
                  const lowAccuracy = row.qaAccuracy < 85;
                  const highSkip = row.skipRate > 10;
                  return (
                    <tr key={row.name}>
                      <td style={{ fontWeight: 600 }}>{row.name}</td>
                      <td style={monoStyle}>{row.tasksToday}</td>
                      <td style={monoStyle}>{row.tasksWeek}</td>
                      <td style={monoStyle}>{row.avgTime}s</td>
                      <td
                        style={{
                          ...monoStyle,
                          color: lowAccuracy ? 'var(--danger)' : undefined,
                          fontWeight: lowAccuracy ? 700 : undefined,
                        }}
                      >
                        {row.qaAccuracy.toFixed(1)}%
                      </td>
                      <td
                        style={{
                          ...monoStyle,
                          color: highSkip ? 'var(--warning)' : undefined,
                          fontWeight: highSkip ? 700 : undefined,
                        }}
                      >
                        {row.skipRate}%
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {row.primaryQueue.replace(/_/g, ' ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== Tab: Queue Operations ===== */}
      {tab === 'queues' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {QUEUE_OPS.map((q, i) => (
            <div key={q.name} style={fadeTransition(visibility[Math.min(i, 7)])}>
              <div className="forge-card">
                <div
                  className="forge-card-title"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <span>{q.displayName}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <RefreshIcon style={{ fontSize: 13 }} />
                    Last regen: {q.lastRegen}
                  </span>
                </div>

                <div
                  className="stats-row"
                  style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 0, gap: 12 }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginBottom: 4,
                      }}
                    >
                      Total Pending
                    </div>
                    <div
                      style={{
                        ...monoStyle,
                        fontSize: 22,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {q.pending.toLocaleString()}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginBottom: 4,
                      }}
                    >
                      <SpeedIcon
                        style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 3 }}
                      />
                      Drain Rate
                    </div>
                    <div
                      style={{
                        ...monoStyle,
                        fontSize: 22,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {q.drainRate}
                      <span
                        style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}
                      >
                        /hr
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginBottom: 4,
                      }}
                    >
                      Skip Rate
                    </div>
                    <div
                      style={{
                        ...monoStyle,
                        fontSize: 22,
                        fontWeight: 700,
                        color: q.skipRate > 10 ? 'var(--warning)' : 'var(--text-primary)',
                      }}
                    >
                      {q.skipRate}%
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginBottom: 4,
                      }}
                    >
                      <ScheduleIcon
                        style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 3 }}
                      />
                      Avg Time
                    </div>
                    <div
                      style={{
                        ...monoStyle,
                        fontSize: 22,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {q.avgTime}
                      <span
                        style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}
                      >
                        s
                      </span>
                    </div>
                  </div>
                </div>

                <QueueMiniBar pending={q.pending} maxPending={maxPending} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
